import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('portal.nrich.chat.panel');
import { OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

declare const wiz: any;

interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    activities?: any[];
    activityStatus?: 'working' | 'done';
    isStreaming?: boolean;
}

interface Session {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
}

@Component({
    selector: 'wiz-portal-nrich-chat-panel',
template: templateSource || '',
    styles: [``],
})
export class PortalNrichChatPanelComponent implements OnInit, OnChanges, OnDestroy {
    @Input() documentId: string = '';
    @Input() pdfName: string = '';
    @Input() currentPage: number = 1;
    @Output() pageChange = new EventEmitter<number>();

    @ViewChild('messagesArea') messagesAreaRef!: ElementRef<HTMLDivElement>;

    public messages: ChatMessage[] = [];
    public sessions: Session[] = [];
    public sessionId: string = '';
    public sessionTitle: string = 'AI 어시스턴트';
    public isStreaming: boolean = false;
    public showSessionDropdown: boolean = false;
    public showSuggestions: boolean = true;

    private abortController: AbortController | null = null;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        if (this.documentId) {
            await this.loadSessions();
        }
        this.addWelcomeMessage();
        await this.service.render();
    }

    public async ngOnChanges(changes: SimpleChanges) {
        // documentId 변경 시 세션 목록 재로드
        if (changes['documentId'] && !changes['documentId'].firstChange && this.documentId) {
            await this.loadSessions();
        }
    }

    public ngOnDestroy() {
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    // ── 세션 관리 ─────────────────────────────────────

    public async loadSessions() {
        try {
            const { code, data } = await wiz.call('sessions', { document_id: this.documentId });
            if (code === 200) {
                this.sessions = data || [];
            }
        } catch (e) {
            console.error('Sessions load error:', e);
        }
    }

    public async createNewSession() {
        try {
            const { code, data } = await wiz.call('session_create', {
                document_id: this.documentId,
                pdf_name: this.pdfName,
            });
            if (code === 200 && data) {
                this.sessionId = data.id;
                this.sessionTitle = data.title || '새 대화';
                this.messages = [];
                this.addWelcomeMessage();
                this.showSuggestions = true;
                await this.loadSessions();
            }
        } catch (e) {
            console.error('Session create error:', e);
        }
        this.showSessionDropdown = false;
        await this.service.render();
    }

    public async switchSession(session: Session) {
        this.sessionId = session.id;
        this.sessionTitle = session.title;
        this.showSessionDropdown = false;

        // 메시지 로드
        try {
            const { code, data } = await wiz.call('session_get', { session_id: session.id });
            if (code === 200) {
                this.messages = (data.messages || []).map((m: any) => ({
                    role: m.role,
                    content: m.content,
                    activities: [],
                    isStreaming: false,
                }));
                this.showSuggestions = this.messages.length <= 1;
            }
        } catch (e) {
            console.error('Session load error:', e);
        }
        await this.service.render();
        this.scrollToBottom();
    }

    public async deleteSession(session: Session, event: MouseEvent) {
        event.stopPropagation();
        try {
            await wiz.call('session_delete', { session_id: session.id });
            if (this.sessionId === session.id) {
                this.sessionId = '';
                this.sessionTitle = 'AI 어시스턴트';
                this.messages = [];
                this.addWelcomeMessage();
                this.showSuggestions = true;
            }
            await this.loadSessions();
        } catch (e) {
            console.error('Session delete error:', e);
        }
        await this.service.render();
    }

    public toggleSessionDropdown() {
        this.showSessionDropdown = !this.showSessionDropdown;
    }

    // ── 메시지 전송 (SSE) ─────────────────────────────

    public async onSend(text: string) {
        if (!text.trim() || this.isStreaming) return;

        // 세션 없으면 자동 생성
        if (!this.sessionId) {
            try {
                const { code, data } = await wiz.call('session_create', {
                    document_id: this.documentId,
                    pdf_name: this.pdfName,
                });
                if (code === 200 && data) {
                    this.sessionId = data.id;
                    this.sessionTitle = data.title || '새 대화';
                }
            } catch (e) {
                console.error('Auto session create error:', e);
                return;
            }
        }

        // 사용자 메시지 추가
        this.messages.push({ role: 'user', content: text });
        this.showSuggestions = false;

        // 어시스턴트 메시지 (스트리밍용)
        const assistantMsg: ChatMessage = {
            role: 'assistant',
            content: '',
            activities: [],
            activityStatus: 'working',
            isStreaming: true,
        };
        this.messages.push(assistantMsg);
        this.isStreaming = true;
        await this.service.render();
        this.scrollToBottom();

        // SSE 스트리밍
        await this.streamChat(text, assistantMsg);
    }

    private async streamChat(message: string, assistantMsg: ChatMessage) {
        this.abortController = new AbortController();

        const formData = new FormData();
        formData.append('document_id', this.documentId);
        formData.append('session_id', this.sessionId);
        formData.append('message', message);
        formData.append('current_page', String(this.currentPage));

        try {
            const response = await fetch(
                `/wiz/api/page.document.item/chat`,
                { method: 'POST', body: formData, signal: this.abortController.signal }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const event = JSON.parse(line.slice(6));
                            this.handleSSEEvent(event, assistantMsg);
                        } catch (e) {
                            // JSON parse error, skip
                        }
                    }
                }
            }
        } catch (e: any) {
            if (e.name !== 'AbortError') {
                assistantMsg.content += '\n\n⚠️ 요청 중 오류가 발생했습니다.';
            }
        } finally {
            assistantMsg.isStreaming = false;
            assistantMsg.activityStatus = 'done';
            this.isStreaming = false;
            this.abortController = null;
            await this.loadSessions(); // 세션 제목 갱신
            await this.service.render();
        }
    }

    private handleSSEEvent(event: any, msg: ChatMessage) {
        if (!msg.activities) msg.activities = [];

        switch (event.type) {
            case 'todo_plan': {
                const tasks = event.tasks || event.todos || [];
                msg.activities = tasks.map((t: any) => ({
                    id: t.exec_id || t.id,
                    task: t.task || t.task_name || t.id,
                    status: t.status === 'done' ? 'done' : 'pending',
                    tags: [],
                }));
                break;
            }
            case 'todo_update': {
                const tasks = event.tasks || event.todos || [];
                const newIds = new Set(tasks.map((t: any) => t.exec_id || t.id));
                // 기존 pending 스텝 중 새 계획에 없는 것 제거
                msg.activities = (msg.activities || []).filter(
                    (a: any) => a.status !== 'pending' || newIds.has(a.id)
                );
                // 새 스텝 추가
                for (const t of tasks) {
                    const key = t.exec_id || t.id;
                    if (!(msg.activities || []).find((a: any) => a.id === key)) {
                        msg.activities!.push({
                            id: key,
                            task: t.task || t.task_name || t.id,
                            status: t.status === 'done' ? 'done' : 'pending',
                            tags: [],
                        });
                    }
                }
                break;
            }
            case 'todo_start': {
                const key = event.exec_id || event.id || event.todo_id;
                const item = (msg.activities || []).find((a: any) => a.id === key);
                if (item) item.status = 'working';
                break;
            }
            case 'todo_progress': {
                const key = event.exec_id || event.id || event.todo_id;
                const item = (msg.activities || []).find((a: any) => a.id === key);
                if (item) item.progress = event.message;
                break;
            }
            case 'todo_done': {
                const key = event.exec_id || event.id || event.todo_id;
                const item = (msg.activities || []).find((a: any) => a.id === key);
                if (item) {
                    item.status = 'done';
                    item.progress = '';
                    // 결과 태그 생성
                    if (event.results?.pages) {
                        item.tags = event.results.pages.slice(0, 10).map((p: any) => ({
                            label: `${p.page}p`,
                            title: p.similarity ? `유사도 ${(p.similarity * 100).toFixed(1)}%` : '',
                            page: p.page,
                        }));
                    }
                    if (event.results?.keywords) {
                        item.tags = (item.tags || []).concat(
                            event.results.keywords.map((k: string) => ({ label: k, title: '검색 키워드' }))
                        );
                    }
                }
                break;
            }
            case 'todo_error': {
                const key = event.exec_id || event.id || event.todo_id;
                const item = (msg.activities || []).find((a: any) => a.id === key);
                if (item) {
                    item.status = 'error';
                    item.error = event.error || '오류 발생';
                }
                break;
            }
            case 'content': {
                msg.content += event.text || '';
                break;
            }
            case 'done': {
                msg.isStreaming = false;
                msg.activityStatus = 'done';
                break;
            }
            case 'error': {
                msg.content += `\n\n⚠️ ${event.message || '오류가 발생했습니다'}`;
                break;
            }
        }
        this.service.render();
        this.scrollToBottom();
    }

    // ── 취소 ──────────────────────────────────────────

    public onCancel() {
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    // ── 유틸 ──────────────────────────────────────────

    public onPageClick(page: number) {
        this.pageChange.emit(page);
    }

    private addWelcomeMessage() {
        this.messages.push({
            role: 'assistant',
            content: '안녕하세요! 문화유산 전문 AI 어시스턴트입니다.\n이 문서에 대해 궁금한 점을 질문해 주세요.',
        });
    }

    private scrollToBottom() {
        setTimeout(() => {
            const el = this.messagesAreaRef?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        }, 50);
    }
}

export default PortalNrichChatPanelComponent;